import * as i0 from '@angular/core';
import { ViewContainerRef, inject, Directive, ChangeDetectorRef, ElementRef, Component, ChangeDetectionStrategy, InjectionToken, isDevMode, Inject, Optional, Self, Input, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { Subject, tap, fromEvent, takeUntil, EMPTY, distinctUntilChanged, merge, startWith, switchMap, map, NEVER } from 'rxjs';
import * as i3 from '@angular/forms';

class ControlErrorAnchorDirective {
    constructor() {
        this.vcr = inject(ViewContainerRef);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: ControlErrorAnchorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.4", type: ControlErrorAnchorDirective, isStandalone: true, selector: "[controlErrorAnchor]", exportAs: ["controlErrorAnchor"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: ControlErrorAnchorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[controlErrorAnchor]',
                    standalone: true,
                    exportAs: 'controlErrorAnchor'
                }]
        }] });

class DefaultControlErrorComponent {
    constructor() {
        this.errorText = null;
        this.hideError = true;
        this.cdr = inject(ChangeDetectorRef);
        this.host = inject(ElementRef);
        this._addClasses = [];
    }
    createTemplate(tpl, error, text) {
        this.errorTemplate = tpl;
        this.errorContext = { $implicit: error, text };
        this.cdr.markForCheck();
    }
    set customClass(classes) {
        if (!this.hideError) {
            this._addClasses = Array.isArray(classes) ? classes : classes.split(/\s/);
            this.host.nativeElement.classList.add(...this._addClasses);
        }
    }
    set text(value) {
        if (value !== this.errorText) {
            this.errorText = value;
            this.hideError = !value;
            if (this.hideError) {
                this.host.nativeElement.classList.remove(...this._addClasses);
            }
            this.cdr.markForCheck();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: DefaultControlErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.0.4", type: DefaultControlErrorComponent, isStandalone: true, selector: "control-error", ngImport: i0, template: `
    @if (!errorTemplate) {
      <label class="control-error" [class.hide-control]="hideError">{{ errorText }}</label>
    }
    <ng-template *ngTemplateOutlet="errorTemplate; context: errorContext"></ng-template>
  `, isInline: true, styles: [".hide-control{display:none!important}:host{display:block}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: DefaultControlErrorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'control-error', standalone: true, imports: [CommonModule], template: `
    @if (!errorTemplate) {
      <label class="control-error" [class.hide-control]="hideError">{{ errorText }}</label>
    }
    <ng-template *ngTemplateOutlet="errorTemplate; context: errorContext"></ng-template>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [".hide-control{display:none!important}:host{display:block}\n"] }]
        }] });

const FORM_ERRORS = new InjectionToken('FORM_ERRORS', {
    providedIn: 'root',
    factory: () => {
        return {};
    },
});
const ErrorTailorConfigProvider = new InjectionToken('ErrorTailorConfigProvider');
function provideErrorTailorConfig(config) {
    return [
        {
            provide: ErrorTailorConfigProvider,
            useValue: config,
        },
        {
            provide: FORM_ERRORS,
            ...config.errors,
        },
    ];
}

class FormActionDirective {
    constructor() {
        this.submit = new Subject();
        this.destroy = new Subject();
        this.host = inject(ElementRef);
        this.element = this.host.nativeElement;
        this.submit$ = this.submit.asObservable();
        this.reset$ = fromEvent(this.element, 'reset').pipe(tap(() => this.submit.next(null)));
    }
    ngOnInit() {
        fromEvent(this.element, 'submit').pipe(takeUntil(this.destroy)).subscribe(this.submit);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: FormActionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.4", type: FormActionDirective, isStandalone: true, selector: "form[errorTailor]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: FormActionDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: 'form[errorTailor]',
                }]
        }] });

const errorTailorClass = 'error-tailor-has-error';
class ControlErrorsDirective {
    constructor(vcr, elementRef, config, globalErrors, controlErrorAnchorParent, form, ngControl, controlContainer) {
        this.vcr = vcr;
        this.config = config;
        this.globalErrors = globalErrors;
        this.controlErrorAnchorParent = controlErrorAnchorParent;
        this.form = form;
        this.ngControl = ngControl;
        this.controlContainer = controlContainer;
        this.customErrors = {};
        this.destroy = new Subject();
        this.mergedConfig = {};
        this.host = elementRef.nativeElement;
        this.submit$ = this.form ? this.form.submit$ : EMPTY;
        this.reset$ = this.form ? this.form.reset$ : EMPTY;
    }
    ngOnInit() {
        this.mergedConfig = this.buildConfig();
        this.control = (this.controlContainer || this.ngControl).control;
        const hasAsyncValidator = !!this.control.asyncValidator;
        const statusChanges$ = this.control.statusChanges.pipe(distinctUntilChanged());
        const valueChanges$ = this.control.valueChanges;
        const controlChanges$ = merge(statusChanges$, valueChanges$);
        let changesOnAsync$ = EMPTY;
        let changesOnBlur$ = EMPTY;
        let changesOnChange$ = EMPTY;
        let changesOnStatusChange$ = EMPTY;
        if (!this.controlErrorsClass || this.controlErrorsClass?.length === 0) {
            if (this.mergedConfig.controlErrorsClass && this.mergedConfig.controlErrorsClass) {
                this.controlErrorsClass = this.mergedConfig.controlErrorsClass;
            }
        }
        if (!this.controlCustomClass || this.controlCustomClass?.length === 0) {
            if (this.mergedConfig.controlCustomClass && this.mergedConfig.controlCustomClass) {
                this.controlCustomClass = this.mergedConfig.controlCustomClass;
            }
        }
        if (this.mergedConfig.controlErrorsOn.async && hasAsyncValidator) {
            // hasAsyncThenUponStatusChange
            changesOnAsync$ = statusChanges$;
        }
        if (this.isInput && this.mergedConfig.controlErrorsOn.change) {
            // on each change
            changesOnChange$ = valueChanges$;
        }
        if (this.mergedConfig.controlErrorsOn.status) {
            changesOnStatusChange$ = statusChanges$;
        }
        if (this.isInput && this.mergedConfig.controlErrorsOn.blur) {
            const blur$ = fromEvent(this.host, 'focusout');
            // blurFirstThenUponChange
            changesOnBlur$ = blur$.pipe(switchMap(() => valueChanges$.pipe(startWith(true))));
        }
        const submit$ = merge(this.submit$.pipe(map(() => true)), this.reset$.pipe(map(() => false), tap(() => this.hideError())));
        // when submitted, submitFirstThenUponChanges
        const changesOnSubmit$ = submit$.pipe(switchMap((submit) => (submit ? controlChanges$.pipe(startWith(true)) : NEVER)));
        // on reset, clear ComponentRef and customAnchorDestroyFn
        this.reset$.pipe(takeUntil(this.destroy)).subscribe(() => this.clearRefs());
        merge(changesOnAsync$, changesOnBlur$, changesOnChange$, changesOnSubmit$, changesOnStatusChange$)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
            const hasErrors = !!this.control.errors;
            if (hasErrors) {
                this.showError();
            }
            else {
                this.hideError();
            }
        });
    }
    setError(text, error) {
        if (this.mergedConfig.controlClassOnly) {
            return;
        }
        this.ref ??= this.resolveAnchor().createComponent(this.mergedConfig.controlErrorComponent);
        const instance = this.ref.instance;
        if (this.controlErrorsTpl) {
            instance.createTemplate(this.controlErrorsTpl, error, text);
        }
        else {
            instance.text = text;
        }
        if (this.controlErrorsClass) {
            instance.customClass = this.controlErrorsClass;
        }
        if (!this.controlErrorAnchor && this.mergedConfig.controlErrorComponentAnchorFn) {
            this.customAnchorDestroyFn = this.mergedConfig.controlErrorComponentAnchorFn(this.host, this.ref.hostView.rootNodes[0]);
        }
    }
    /**
     * Explicit showing of a control error via some custom application code.
     */
    showError() {
        const controlErrors = this.control.errors;
        if (controlErrors) {
            const [firstKey] = Object.keys(controlErrors);
            const getError = this.customErrors[firstKey] || this.globalErrors[firstKey];
            if (!getError) {
                if (isDevMode()) {
                    console.warn(`[@ngneat/error-tailor]: Missing error message for ${firstKey}`);
                }
                return;
            }
            const text = typeof getError === 'function' ? getError(controlErrors[firstKey]) : getError;
            this.addCustomClass();
            this.setError(text, controlErrors);
        }
    }
    /**
     * Explicit hiding of a control error via some custom application code.
     */
    hideError() {
        this.removeCustomClass();
        if (this.ref) {
            this.setError(null);
        }
    }
    ngOnDestroy() {
        this.destroy.next();
        this.clearRefs();
    }
    get customClasses() {
        return Array.isArray(this.controlCustomClass)
            ? this.controlCustomClass
            : this.controlCustomClass?.split(/\s/) ?? [];
    }
    get isInput() {
        return this.mergedConfig.blurPredicate(this.host);
    }
    clearRefs() {
        if (this.customAnchorDestroyFn) {
            this.customAnchorDestroyFn();
            this.customAnchorDestroyFn = null;
        }
        this.ref?.destroy();
        this.ref = null;
    }
    resolveAnchor() {
        if (this.controlErrorAnchor) {
            return this.controlErrorAnchor.vcr;
        }
        if (this.controlErrorAnchorParent) {
            return this.controlErrorAnchorParent.vcr;
        }
        return this.vcr;
    }
    buildConfig() {
        return {
            ...{
                blurPredicate(element) {
                    return element.tagName === 'INPUT' || element.tagName === 'SELECT';
                },
                controlErrorComponent: DefaultControlErrorComponent,
            },
            ...this.config,
            controlErrorsOn: {
                async: this.controlErrorsOnAsync ?? this.config.controlErrorsOn?.async ?? true,
                blur: this.controlErrorsOnBlur ?? this.config.controlErrorsOn?.blur ?? true,
                change: this.controlErrorsOnChange ?? this.config.controlErrorsOn?.change ?? false,
                status: this.controlErrorsOnStatusChange ?? this.config.controlErrorsOn?.status ?? false,
            },
        };
    }
    addCustomClass() {
        if (this.isInput) {
            this.host.parentElement.classList.add(errorTailorClass);
            if (this.controlCustomClass) {
                this.host.classList.add(...this.customClasses);
            }
        }
    }
    removeCustomClass() {
        if (this.isInput) {
            this.host.parentElement.classList.remove(errorTailorClass);
            if (this.controlCustomClass) {
                this.host.classList.remove(...this.customClasses);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: ControlErrorsDirective, deps: [{ token: i0.ViewContainerRef }, { token: i0.ElementRef }, { token: ErrorTailorConfigProvider }, { token: FORM_ERRORS }, { token: ControlErrorAnchorDirective, optional: true }, { token: FormActionDirective, optional: true }, { token: i3.NgControl, optional: true, self: true }, { token: i3.ControlContainer, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.4", type: ControlErrorsDirective, isStandalone: true, selector: "[formControlName]:not([controlErrorsIgnore]), [formControl]:not([controlErrorsIgnore]), [formGroup]:not([controlErrorsIgnore]), [formGroupName]:not([controlErrorsIgnore]), [formArrayName]:not([controlErrorsIgnore]), [ngModel]:not([controlErrorsIgnore])", inputs: { customErrors: ["controlErrors", "customErrors"], controlErrorsClass: "controlErrorsClass", controlCustomClass: "controlCustomClass", controlErrorsTpl: "controlErrorsTpl", controlErrorsOnAsync: "controlErrorsOnAsync", controlErrorsOnBlur: "controlErrorsOnBlur", controlErrorsOnChange: "controlErrorsOnChange", controlErrorsOnStatusChange: "controlErrorsOnStatusChange", controlErrorAnchor: "controlErrorAnchor" }, exportAs: ["errorTailor"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: ControlErrorsDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[formControlName]:not([controlErrorsIgnore]), [formControl]:not([controlErrorsIgnore]), [formGroup]:not([controlErrorsIgnore]), [formGroupName]:not([controlErrorsIgnore]), [formArrayName]:not([controlErrorsIgnore]), [ngModel]:not([controlErrorsIgnore])',
                    exportAs: 'errorTailor',
                }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ErrorTailorConfigProvider]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FORM_ERRORS]
                }] }, { type: ControlErrorAnchorDirective, decorators: [{
                    type: Optional
                }] }, { type: FormActionDirective, decorators: [{
                    type: Optional
                }] }, { type: i3.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i3.ControlContainer, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }], propDecorators: { customErrors: [{
                type: Input,
                args: ['controlErrors']
            }], controlErrorsClass: [{
                type: Input
            }], controlCustomClass: [{
                type: Input
            }], controlErrorsTpl: [{
                type: Input
            }], controlErrorsOnAsync: [{
                type: Input
            }], controlErrorsOnBlur: [{
                type: Input
            }], controlErrorsOnChange: [{
                type: Input
            }], controlErrorsOnStatusChange: [{
                type: Input
            }], controlErrorAnchor: [{
                type: Input
            }] } });

const _errorTailorImports = [
    ControlErrorsDirective,
    ControlErrorAnchorDirective,
    DefaultControlErrorComponent,
    FormActionDirective,
];
class errorTailorImports {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: errorTailorImports, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.4", ngImport: i0, type: errorTailorImports, imports: [ControlErrorsDirective,
            ControlErrorAnchorDirective,
            DefaultControlErrorComponent,
            FormActionDirective], exports: [ControlErrorsDirective,
            ControlErrorAnchorDirective,
            DefaultControlErrorComponent,
            FormActionDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: errorTailorImports, imports: [DefaultControlErrorComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: errorTailorImports, decorators: [{
            type: NgModule,
            args: [{
                    imports: [_errorTailorImports],
                    exports: [_errorTailorImports],
                }]
        }] });

/*
 * Public API Surface of error-tailor
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ControlErrorAnchorDirective, ControlErrorsDirective, DefaultControlErrorComponent, ErrorTailorConfigProvider, FORM_ERRORS, FormActionDirective, errorTailorImports, provideErrorTailorConfig };
